import { useMemo, useState } from 'react';
import { Card, CardContent } from '../../ui';
import { RISK_BANDS, riskBand } from '../../../constants/risk';
import { formatUTCDate } from '../../../utils/dates';
import { cn } from '../../../utils/cn';

/**
 * Where each account's exposure stands, and which way it moved.
 *
 * Two rewrites, and the second was needed because the first still could not be
 * read at a glance. The history:
 *
 * The dashboard original drew ONE line through every scored scan in the
 * workspace in scan order, so consecutive points belonged to different
 * accounts and the slope was an artifact of who happened to be scanned next.
 *
 * Replacing it with a line per account was correct and still unreadable, for a
 * reason only the real data shows: of six accounts, FOUR sit flat at zero. The
 * chart was four identical lines overlapping along the floor, one wiggle near
 * 77, and a couple of isolated dots, spread over an axis where two months of
 * span held four days of actual data. A time series needs points over time to
 * say anything, and this data is one to eight scans per account at irregular
 * intervals.
 *
 * The question a manager opens this for is not "what shape was the month". It
 * is "who is risky, and who is getting worse". So each account is a row on a
 * shared 0-100 track with the risk bands painted behind it: a filled dot for
 * where they are now, and a tail back to where they started if that differs.
 * Four accounts at zero become four dots in the green zone, which reads as
 * "these are fine" instantly instead of as mush.
 *
 * Time moves to the tooltip and the header, where it is context rather than a
 * dimension the data cannot support.
 *
 * No chart library. The problem here was never rendering: it was choosing a
 * form the data could carry. Recharts would have drawn the same unreadable
 * lines, and at roughly a third of the app's gzipped bundle, on a single
 * bundle with no code splitting, so it would have landed on the marketing page
 * too.
 */

const MAX_ROWS = 8;

/** Scored scans only, grouped by account, oldest first. */
export function buildSeries(scans = []) {
	const byAccount = new Map();
	for (const s of scans) {
		if (!s?.hasScore || typeof s.riskScore !== 'number') continue;
		const at = new Date(s.startedAt || s.createdAt).getTime();
		if (Number.isNaN(at)) continue;
		// Two shapes reach this: the raw API scan (account is populated) and the
		// scans page's normalized row (flattened to accountId/accountHandle).
		const id = s.accountId || s.account?._id || s.account?.handle || s.accountHandle || 'unknown';
		const handle = s.account?.handle || s.accountHandle || 'unknown';
		if (!byAccount.has(id)) byAccount.set(id, { id, handle, points: [] });
		byAccount.get(id).points.push({ at, score: s.riskScore, id: s._id });
	}
	const series = [...byAccount.values()];
	for (const s of series) {
		s.points.sort((a, b) => a.at - b.at);
		s.latest = s.points[s.points.length - 1];
		s.first = s.points[0];
		// Only a real comparison: one point has no direction.
		s.change = s.points.length > 1 ? s.latest.score - s.first.score : null;
	}
	// Worst current exposure first, because that is the row a manager came for.
	series.sort((a, b) => b.latest.score - a.latest.score);
	return series;
}

/** The sentence above the chart. States the finding, or says there isn't one. */
export function headline(series) {
	if (!series.length) return null;
	const movers = series.filter((s) => s.change !== null && s.change !== 0);
	const worst = series[0];
	const risen = movers.filter((s) => s.change > 0).sort((a, b) => b.change - a.change);
	const fallen = movers.filter((s) => s.change < 0).sort((a, b) => a.change - b.change);

	if (risen.length) {
		const top = risen[0];
		return `@${top.handle} is up ${top.change} points since their first scan here, to ${top.latest.score}.`;
	}
	if (fallen.length) {
		const top = fallen[0];
		return `@${top.handle} is down ${Math.abs(top.change)} points since their first scan here, to ${top.latest.score}.`;
	}
	// "Nothing has moved" is wrong when nothing COULD have: an account with one
	// scored scan has no direction to report, and saying it held steady implies
	// a comparison that was never made.
	if (!series.some((s) => s.points.length > 1)) {
		return `One scored scan each so far, so there is no direction to read yet. Highest is @${worst.handle} at ${worst.latest.score}.`;
	}
	const atRisk = series.filter((s) => s.latest.score >= 60).length;
	if (atRisk) {
		return `Nothing has moved. ${atRisk} account${atRisk === 1 ? ' is' : 's are'} at risk or worse, highest @${worst.handle} at ${worst.latest.score}.`;
	}
	return `Nothing has moved, and nothing is above the clear band. Highest is @${worst.handle} at ${worst.latest.score}.`;
}

/** Band zones as percentage widths of the 0-100 track, low to high. */
const ZONES = [...RISK_BANDS].reverse().map((b) => ({
	key: b.key,
	label: b.label,
	width: ((b.max - b.min + 1) / 101) * 100,
	fill: b.key === 'clear' ? 'bg-green-500/[0.09]'
		: b.key === 'watch' ? 'bg-amber-400/[0.11]'
		: b.key === 'atRisk' ? 'bg-orange-500/[0.11]'
		: 'bg-red-500/[0.11]',
}));

/** Score, band and movement. One instance, placed by grid at both widths. */
function Readout({ s, band, moved }) {
	return (
		// Placed rather than duplicated: rendering a mobile copy and a desktop
		// copy and hiding one with CSS puts every number in the DOM twice, and a
		// screen reader reads both.
		<div className="col-start-2 row-start-1 flex items-center gap-2 justify-self-end whitespace-nowrap text-sm sm:col-start-3 sm:row-start-1">
			<span className="font-semibold tabular-nums text-foreground">{s.latest.score}</span>
			{/* band.label, not riskLabel: that helper appends the score, which this
			    row already prints beside it. */}
			<span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', band?.badge)}>{band?.label}</span>
			{moved ? (
				<span className={cn('text-xs font-medium tabular-nums', s.change > 0 ? 'text-red-600' : 'text-green-700')}>
					{s.change > 0 ? '+' : ''}{s.change}
				</span>
			) : (
				<span className="text-xs text-muted-foreground">{s.change === null ? 'one scan' : 'no change'}</span>
			)}
		</div>
	);
}

function Row({ s, onHover, hovered }) {
	const band = riskBand(s.latest.score);
	const moved = s.change !== null && s.change !== 0;
	// The tail runs from where they started to where they are now, so its
	// length is the movement and its direction is visible without reading a
	// number.
	const from = Math.min(s.first.score, s.latest.score);
	const to = Math.max(s.first.score, s.latest.score);

	return (
		<div
			// On a phone the track takes its own full-width line. Sharing a row with
			// the handle squeezed the whole 0-100 scale into about 160px, where two
			// dots eight points apart look identical.
			className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 py-2.5 sm:grid-cols-[minmax(7rem,10rem)_1fr_11rem] sm:items-center sm:gap-y-0 sm:py-2"
			onMouseEnter={() => onHover(s.id)}
			onMouseLeave={() => onHover(null)}
		>
			<span className="col-start-1 row-start-1 min-w-0 self-center truncate text-sm font-medium text-foreground" title={`@${s.handle}`}>
				@{s.handle}
			</span>

			<div className="relative col-span-2 row-start-2 h-7 sm:col-span-1 sm:col-start-2 sm:row-start-1">
				{/* Painted bands: a dot's position is a verdict without a legend. */}
				<div className="absolute inset-x-0 top-1/2 flex h-2.5 -translate-y-1/2 overflow-hidden rounded-full">
					{ZONES.map((z) => <span key={z.key} className={cn('h-full', z.fill)} style={{ width: `${z.width}%` }} />)}
				</div>

				{moved && (
					<span
						className={cn('absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full', s.change > 0 ? 'bg-red-400/70' : 'bg-green-500/70')}
						style={{ left: `${from}%`, width: `${Math.max(0, to - from)}%` }}
					/>
				)}
				{moved && (
					<span
						className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-input bg-card"
						style={{ left: `${s.first.score}%` }}
						title={`First scan: ${s.first.score} on ${formatUTCDate(s.first.at)}`}
					/>
				)}
				<span
					className={cn('absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-white transition-transform', band?.dot, hovered && 'scale-125')}
					style={{ left: `${s.latest.score}%` }}
					title={`Latest: ${s.latest.score} on ${formatUTCDate(s.latest.at)}`}
				/>
			</div>

			<Readout s={s} band={band} moved={moved} />
		</div>
	);
}

export function ExposureTrend({ scans = [], className }) {
	const [hovered, setHovered] = useState(null);
	const all = useMemo(() => buildSeries(scans), [scans]);
	const rows = all.slice(0, MAX_ROWS);
	const hidden = all.length - rows.length;

	const lastScored = useMemo(() => {
		const times = all.flatMap((s) => s.points.map((p) => p.at));
		return times.length ? Math.max(...times) : null;
	}, [all]);

	if (!rows.length) {
		return (
			<Card className={className}>
				<CardContent className="p-4 sm:p-5">
					<h2 className="text-base font-semibold text-foreground">Exposure by account</h2>
					<p className="mt-2 text-sm text-muted-foreground">
						No scored scans here yet. A scan that analysed no posts has no exposure score, so it is not shown.
					</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className={className}>
			<CardContent className="p-4 sm:p-5">
				<div className="flex flex-wrap items-baseline justify-between gap-2">
					<h2 className="text-base font-semibold text-foreground">Exposure by account</h2>
					<span className="text-xs text-muted-foreground">
						{all.length} account{all.length === 1 ? '' : 's'} scored
						{lastScored && `, latest ${formatUTCDate(lastScored)}`}
						{hidden > 0 && `, showing the ${MAX_ROWS} highest`}
					</span>
				</div>
				<p className="mt-1 text-sm text-foreground">{headline(all)}</p>

				{/* Band names once, above the track, aligned to their zones. */}
				<div className="mt-4 hidden gap-3 sm:grid sm:grid-cols-[minmax(7rem,10rem)_1fr_11rem]">
					<span />
					<div className="flex text-[10px] uppercase tracking-wide text-muted-foreground">
						{ZONES.map((z) => (
							<span key={z.key} className="truncate pl-0.5" style={{ width: `${z.width}%` }}>{z.label}</span>
						))}
					</div>
					<span className="hidden sm:block" />
				</div>

				<div className="divide-y divide-border border-t border-border">
					{rows.map((s) => (
						<Row key={s.id} s={s} hovered={hovered === s.id} onHover={setHovered} />
					))}
				</div>

				<p className="mt-3 text-xs text-muted-foreground">
					Each dot is where an account stands now. A tail shows where it started, so its length is the movement.
					Hover a dot for the date.
				</p>
			</CardContent>
		</Card>
	);
}
